import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LogAnalyticsIngestionCalc from '../components/calculators/LogAnalyticsIngestionCalc';
import LogAnalyticsRetentionCalc from '../components/calculators/LogAnalyticsRetentionCalc';
import LogAnalyticsSearchJobCalc from '../components/calculators/LogAnalyticsSearchJobCalc';
import LogAnalyticsRestoreCalc from '../components/calculators/LogAnalyticsRestoreCalc';
import LogAnalyticsPreDeployCalc from '../components/calculators/LogAnalyticsPreDeployCalc';
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper';
import { generateBreadcrumbList } from '../utils/schemaHelpers';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';

type Tab = 'ingestion' | 'retention' | 'search-job' | 'restore' | 'pre-deploy';

interface TabDef {
  id: Tab
  label: string
  icon: string
  warn?: boolean
  description: string
}

const TABS: TabDef[] = [
  {
    id: 'ingestion',
    label: 'Ingestion Cost',
    icon: 'cloud_upload',
    description: 'Estimate Analytics, Basic, and Auxiliary log ingestion costs with optional Sentinel pricing.',
  },
  {
    id: 'retention',
    label: 'Retention Cost',
    icon: 'storage',
    description: 'Model interactive and archive retention costs. Sentinel extends free retention to 90 days.',
  },
  {
    id: 'search-job',
    label: 'Search Job',
    icon: 'search',
    description: 'Compare Search Job cost vs Restore for ad-hoc queries over archived data.',
  },
  {
    id: 'restore',
    label: 'Restore',
    icon: 'restore',
    warn: true,
    description: 'Calculate data restore costs. Note: Azure bills a 2 TB minimum — see the warning below.',
  },
  {
    id: 'pre-deploy',
    label: 'Pre-deployment Estimate',
    icon: 'rocket_launch',
    description: 'Estimate Log Analytics ingestion before your workspace is live. Enter resource counts to get a volume and cost forecast.',
  },
];

export default function LogAnalyticsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('ingestion');
  const [searchParams] = useSearchParams();
  const current = TABS.find((t) => t.id === activeTab)!;
  const rawPrefill = Number(searchParams.get('prefill') ?? '');
  const prefillDailyIngestionGB = Number.isFinite(rawPrefill) && rawPrefill > 0 ? rawPrefill : undefined;

  return (
    <div className="px-6 py-6 w-full">
      <Helmet>
        <title>Azure Log Analytics Cost Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Calculate Azure Log Analytics and Microsoft Sentinel costs for UK South. Model ingestion, retention, search jobs, and data restore. Updated nightly from Azure Retail Prices API." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/log-analytics" />
        <meta property="og:title" content="Azure Log Analytics Cost Calculator UK" />
        <meta property="og:description" content="Free Log Analytics and Sentinel cost estimator for UK South. Model ingestion, retention, search jobs and data restore in GBP." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/log-analytics" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Azure Log Analytics Cost Calculator UK',
          description: 'Calculate Azure Log Analytics and Microsoft Sentinel costs for UK South. Model ingestion, retention, search jobs, and data restore.',
          url: 'https://www.azure-calc.co.uk/log-analytics',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([{ name: 'Home', url: '/', position: 1 }, { name: 'Azure Log Analytics Calculator', url: '/log-analytics', position: 2 }]))}</script>
      </Helmet>

      {/* Page header */}
      <div className="pb-6 mb-8" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
        <Breadcrumbs />
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Azure Log Analytics &amp; Sentinel Cost Calculator</h1>
        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>UK South pricing · Ingestion · Retention · Search Jobs · Restore · Pre-deployment estimate</p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-outline-variant/40 mb-8 overflow-x-auto">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-all border-b-2 -mb-px ${isActive
                ? tab.warn
                  ? 'border-red-500 text-red-400'
                  : 'border-primary text-primary'
                : 'border-transparent text-zinc-400 hover:text-on-surface hover:border-outline-variant'
                }`}
            >
              <span
                className={`material-symbols-outlined text-[18px] ${isActive ? (tab.warn ? 'text-red-400' : 'text-primary') : 'text-zinc-500'}`}
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                {tab.icon}
              </span>
              {tab.label}
              {tab.warn && (
                <span className="material-symbols-outlined text-[14px] text-red-400" style={{ fontVariationSettings: "'FILL' 1" }}>
                  warning
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab description */}
      <div className="mb-8 flex items-start gap-3 bg-surface-container-high/50 border border-outline-variant/30 rounded-lg p-4">
        <span
          className={`material-symbols-outlined text-xl shrink-0 ${activeTab === 'restore' ? 'text-red-400' : 'text-primary'}`}
          style={{ fontVariationSettings: "'FILL' 1" }}
        >
          {current.icon}
        </span>
        <p className="text-sm text-zinc-400 leading-relaxed">{current.description}</p>
      </div>

      {/* Active calculator */}
      {activeTab === 'ingestion' && <LogAnalyticsIngestionCalc key={prefillDailyIngestionGB ?? 'default'} initialDailyIngestionGB={prefillDailyIngestionGB} />}
      {activeTab === 'retention' && <LogAnalyticsRetentionCalc />}
      {activeTab === 'search-job' && <LogAnalyticsSearchJobCalc />}
      {activeTab === 'restore' && <LogAnalyticsRestoreCalc />}
      {activeTab === 'pre-deploy' && <LogAnalyticsPreDeployCalc />}

      <CalculatorContentWrapper
        eyebrow="Observability FinOps"
        title="Why Log Analytics pricing needs more than a simple per-GB estimate"
        intro={
          <>
            <p className="mb-3">
              Log Analytics cost becomes difficult when teams treat telemetry as passive background
              data. In reality, Azure Monitor and Sentinel spend is driven by ingestion volume,
              retention policy, table type, investigation workflow, and the way security or operations
              teams query historic data. A low-friction observability rollout can become expensive if
              workspace growth is not designed deliberately.
            </p>
            <p>
              The calculators on this page model the main charging surfaces separately so you can test
              the operational choices behind the bill rather than relying on a single blended number.
            </p>
          </>
        }
        sections={[
          {
            id: 'billing-model',
            title: 'Ingestion is usually the starting point, but not the whole invoice',
            body: (
              <>
                <p className="mb-3">
                  Most teams first encounter Log Analytics pricing through ingestion charges, because
                  that is the clearest ongoing meter. Data volume per day or per month still matters,
                  but table plan choice also matters. Analytics, Basic, and Auxiliary data are not
                  intended for the same query patterns or retention assumptions.
                </p>
                <p>
                  If you price everything as Analytics by default, the estimate may be conservative but
                  operationally blunt. If you push too much into cheaper plans without understanding the
                  query consequences, the bill may look lower while investigation quality suffers.
                </p>
              </>
            ),
          },
          {
            id: 'retention-logic',
            title: 'Retention, archive, search jobs, and restore each serve different use cases',
            body: (
              <>
                <p className="mb-3">
                  Retention is not a single switch. Interactive retention keeps data readily available
                  for normal query workflows, while archive-oriented access patterns depend on search
                  jobs or restore operations. Those choices should match how your platform, security,
                  and compliance teams actually work with older data.
                </p>
                <p>
                  If historic logs are rarely touched, archive-style approaches can make sense. If
                  investigators repeatedly need fast ad hoc access, the cheapest retention choice on
                  paper may create friction and extra operational cost later.
                </p>
              </>
            ),
          },
          {
            id: 'commitment-tiers',
            title: 'Commitment tiers reward stable volume, but they only work when your baseline is real',
            body: (
              <>
                <p className="mb-3">
                  Commitment tiers can be powerful for mature environments with predictable ingestion,
                  because they shift pricing away from pure retail per-GB behavior. The risk is taking
                  a commitment based on temporary peaks, migration noise, or poor data hygiene and then
                  locking in spend that the estate does not actually justify.
                </p>
                <p>
                  Use the pre-deployment and ingestion calculators together when you are still
                  estimating volume. That gives you a better signal for whether a commitment model is a
                  sensible FinOps move or just a premature optimisation.
                </p>
              </>
            ),
          },
          {
            id: 'operational-use-cases',
            title: 'Real operational use cases change the right cost model',
            body: (
              <>
                <p className="mb-3">
                  A platform team troubleshooting App Service and VM incidents has different log access
                  needs from a security team running Sentinel detections or an engineering team keeping
                  long retention for audit evidence. Those use cases affect how much data must stay
                  interactive, how quickly old logs need to be searched, and whether restore actions
                  are realistic in an incident response process.
                </p>
                <p>
                  That is why this page separates ingestion, retention, search jobs, restore, and
                  pre-deployment modelling into distinct tools. They map more closely to operational
                  reality than a single logs cost number.
                </p>
              </>
            ),
          },
          {
            id: 'cost-control',
            title: 'Practical ways to control observability spend',
            body: (
              <ul className="list-disc pl-5 space-y-2">
                <li>Filter or reduce noisy data sources before they hit the workspace instead of paying to ingest them forever.</li>
                <li>Match table plan and retention settings to the real investigation value of the data.</li>
                <li>Review commitment tiers only after daily volume stabilises and temporary spikes are understood.</li>
                <li>Use search jobs or restore selectively, based on response workflow, rather than treating them as interchangeable.</li>
                <li>Revisit Sentinel assumptions separately because security analytics can materially change volume and retention needs.</li>
              </ul>
            ),
          },
          {
            id: 'faq',
            title: 'Quick FAQ',
            body: (
              <>
                <p className="mb-2"><strong>Is ingestion the only major Log Analytics cost?</strong> No. Retention, search jobs, restore, and Sentinel-related decisions can materially affect spend.</p>
                <p className="mb-2"><strong>When do commitment tiers make sense?</strong> When your ingestion baseline is stable enough that discounted committed volume is lower risk than pure retail pricing.</p>
                <p><strong>What should teams validate after using these calculators?</strong> Actual daily volume, table mix, retention requirements, query frequency, and whether old-data investigations need search jobs or restore access.</p>
              </>
            ),
          },
        ]}
        methodologyNote={
          <>
            <p className="mb-2">
              AzureCalc.uk uses UK South retail pricing in GBP and keeps the estimate logic explicit on
              the page. Methodology notes cover source data, update cadence, and the reasons a live
              Azure estate may differ because of agreements, architecture, or ingestion mix.
            </p>
            <Link to="/methodology" className="text-[#0078d4] hover:underline">
              Read the methodology →
            </Link>
          </>
        }
        relatedLinks={[
          { label: 'KQL Query Builder', to: '/kql-builder' },
          { label: 'Log Analytics Pricing Guide', to: '/guides/azure-log-analytics-pricing-uk' },
          { label: 'Search Job vs Restore Guide', to: '/guides/log-analytics-search-job-vs-restore' },
          { label: 'Methodology', to: '/methodology' },
        ]}
      />

      <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>Related guides</h2>
        <div className="flex flex-col gap-3">
          {[
            {
              to: '/guides/azure-log-analytics-pricing-uk',
              title: 'Azure Log Analytics Pricing UK 2026',
              desc: 'Commitment tiers, per-GB billing, retention costs, and Sentinel add-on pricing explained',
            },
            {
              to: '/guides/log-analytics-search-job-vs-restore',
              title: 'Search Job vs Restore — which to use?',
              desc: 'When to run a Search Job vs a full Data Restore, and how to minimise cost for each',
            },
          ].map(({ to, title, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-4 rounded transition-colors group"
              style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
            >
              <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
              <div>
                <p className="text-[14px] font-semibold group-hover:text-white transition-colors" style={{ color: 'var(--text-secondary)' }}>{title}</p>
                <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{desc}</p>
              </div>
              <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
